'use client';

import { useState, useCallback, useEffect } from 'react';
import { Button } from '@myapp/ui';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@myapp/ui';
import { ToggleGroup, ToggleGroupItem } from '@myapp/ui';
import { Badge } from '@myapp/ui';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@myapp/ui';
import { Calendar, Sun, Snowflake, Flower, Leaf, RotateCcw, X, Filter, ChevronDown } from 'lucide-react';
import { cn } from '@myapp/ui';
import { usePrefersReducedMotion } from '@/hooks/use-accessibility';

export interface FilterValues {
  region: string | null;
  month: number | null;
  season: string | null;
}

interface FilterBarProps {
  value: FilterValues;
  onChange: (value: FilterValues) => void;
  className?: string;
}

const months = [
  { value: 1, label: '1월' },
  { value: 2, label: '2월' },
  { value: 3, label: '3월' },
  { value: 4, label: '4월' },
  { value: 5, label: '5월' },
  { value: 6, label: '6월' },
  { value: 7, label: '7월' },
  { value: 8, label: '8월' },
  { value: 9, label: '9월' },
  { value: 10, label: '10월' },
  { value: 11, label: '11월' },
  { value: 12, label: '12월' },
];

const seasons = [
  { value: 'spring', label: '봄', icon: Flower, months: [3, 4, 5] },
  { value: 'summer', label: '여름', icon: Sun, months: [6, 7, 8] },
  { value: 'autumn', label: '가을', icon: Leaf, months: [9, 10, 11] },
  { value: 'winter', label: '겨울', icon: Snowflake, months: [12, 1, 2] },
];

export function FilterBar({ value, onChange, className }: FilterBarProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();
  
  // Auto-select current month on mount
  useEffect(() => {
    if (!value.month && !value.season) {
      const currentMonth = new Date().getMonth() + 1;
      onChange({ ...value, month: currentMonth });
    }
  }, []);

  const handleMonthChange = useCallback((monthValue: string) => {
    const month = monthValue && monthValue !== 'all' ? parseInt(monthValue) : null;
    onChange({ 
      ...value, 
      month,
      season: null // Clear season when month is selected
    });
  }, [value, onChange]);

  const handleSeasonChange = useCallback((seasonValue: string) => {
    onChange({ 
      ...value, 
      season: seasonValue || null,
      month: null // Clear month when season is selected
    });
  }, [value, onChange]);

  const handleReset = useCallback(() => {
    onChange({ 
      region: null, 
      month: null, 
      season: null 
    });
  }, [onChange]);

  const handleClearFilter = useCallback((filterType: 'region' | 'month' | 'season') => {
    onChange({ 
      ...value, 
      [filterType]: null 
    });
  }, [value, onChange]);

  // Get active filter count
  const activeFilterCount = [value.region, value.month, value.season].filter(Boolean).length;

  // Get season from month
  const getSeasonFromMonth = (month: number) => {
    return seasons.find(s => s.months.includes(month))?.label;
  };

  return (
    <div className={cn("space-y-4", className)}>
      {/* Mobile Filter Menu */}
      <div className="flex items-center justify-between gap-3 lg:hidden">
        <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="flex-1 justify-between"
              aria-label="필터 메뉴 열기"
            >
              <span className="flex items-center gap-2">
                <Filter className="w-4 h-4" />
                필터
              </span>
              {activeFilterCount > 0 && (
                <Badge variant="secondary" className="ml-1">
                  {activeFilterCount}
                </Badge>
              )}
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-[80vh]">
            <SheetHeader>
              <SheetTitle>필터 선택</SheetTitle>
            </SheetHeader>
            <div className="mt-6 space-y-6">
              {/* Month Selector in Mobile */}
              <div className="space-y-3">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  월 선택
                </label>
                <Select 
                  value={value.month?.toString() || 'all'} 
                  onValueChange={handleMonthChange}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="월을 선택하세요" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">전체</SelectItem>
                    {months.map((month) => (
                      <SelectItem key={month.value} value={month.value.toString()}>
                        {month.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Season Selector in Mobile */}
              <div className="space-y-3">
                <label className="text-sm font-medium">계절 선택</label>
                <div className="grid grid-cols-2 gap-2">
                  {seasons.map((season) => {
                    const Icon = season.icon;
                    const isSelected = value.season === season.value;
                    return (
                      <Button
                        key={season.value}
                        variant={isSelected ? "default" : "outline"}
                        onClick={() => handleSeasonChange(isSelected ? '' : season.value)}
                        className="justify-start gap-2"
                      >
                        <Icon className="w-4 h-4" />
                        {season.label}
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Tips in Mobile */}
              <div className="p-4 bg-muted/30 rounded-lg space-y-2 text-sm">
                <p className="font-medium text-foreground">🌿 제철 특산물 찾기</p>
                <ul className="space-y-1 text-muted-foreground ml-4">
                  <li>• <strong>봄</strong>: 산나물, 봄나물, 딸기</li>
                  <li>• <strong>여름</strong>: 고랭지 채소, 옥수수</li>
                  <li>• <strong>가을</strong>: 버섯, 사과, 단풍나물</li>
                  <li>• <strong>겨울</strong>: 황태, 메밀, 산나물</li>
                </ul>
              </div>

              {/* Reset Button in Mobile */}
              {activeFilterCount > 0 && (
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="w-full gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  모든 필터 초기화
                </Button>
              )}
            </div>
          </SheetContent>
        </Sheet>

        {/* Quick Season Toggle for Mobile */}
        <div className="flex gap-1">
          {seasons.map((season) => {
            const Icon = season.icon;
            const isSelected = value.season === season.value;
            return (
              <button
                key={season.value}
                onClick={() => handleSeasonChange(isSelected ? '' : season.value)}
                className={cn(
                  "p-2 rounded-lg transition-colors",
                  "hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring",
                  isSelected && "bg-primary text-primary-foreground"
                )}
                aria-label={`${season.label} 필터 ${isSelected ? '제거' : '적용'}`}
              >
                <Icon className="w-4 h-4" />
              </button>
            );
          })}
        </div>
      </div>

      {/* Desktop Filter Controls */}
      <div className="hidden lg:flex flex-wrap items-center gap-3">
        {/* Month Selector */}
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <Select 
            value={value.month?.toString() || ''} 
            onValueChange={handleMonthChange}
          >
            <SelectTrigger 
              className="w-[120px] sm:w-[140px]"
              aria-label="월 선택"
            >
              <SelectValue placeholder="월 선택" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="">전체</SelectItem>
              {months.map((month) => (
                <SelectItem key={month.value} value={month.value.toString()}>
                  {month.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Season Toggle */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">또는</span>
          <ToggleGroup 
            type="single" 
            value={value.season || ''} 
            onValueChange={handleSeasonChange}
            className="gap-1"
            aria-label="계절 선택"
          >
            {seasons.map((season) => {
              const Icon = season.icon;
              return (
                <ToggleGroupItem 
                  key={season.value} 
                  value={season.value}
                  aria-label={`${season.label} 선택`}
                  className="gap-1 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground"
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden sm:inline">{season.label}</span>
                </ToggleGroupItem>
              );
            })}
          </ToggleGroup>
        </div>

        {/* Advanced Filters Toggle */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="gap-1 ml-auto sm:ml-0"
        >
          <Filter className="w-4 h-4" />
          <span className="hidden sm:inline">필터</span>
          {activeFilterCount > 0 && (
            <Badge variant="secondary" className="ml-1 h-5 px-1">
              {activeFilterCount}
            </Badge>
          )}
        </Button>

        {/* Reset Button */}
        {activeFilterCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            className="gap-1"
            aria-label="필터 초기화"
          >
            <RotateCcw className="w-4 h-4" />
            <span className="hidden sm:inline">초기화</span>
          </Button>
        )}
      </div>

      {/* Active Filters Display */}
      {activeFilterCount > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm text-muted-foreground hidden sm:inline">활성 필터:</span>
          
          {value.region && (
            <Badge 
              variant="secondary" 
              className="gap-1 pr-1"
            >
              지역: {value.region}
              <button
                onClick={() => handleClearFilter('region')}
                className="ml-1 rounded-full hover:bg-background/20 p-0.5"
                aria-label={`${value.region} 필터 제거`}
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
          
          {value.month && (
            <Badge 
              variant="secondary" 
              className="gap-1 pr-1"
            >
              {value.month}월 ({getSeasonFromMonth(value.month)})
              <button
                onClick={() => handleClearFilter('month')}
                className="ml-1 rounded-full hover:bg-background/20 p-0.5"
                aria-label={`${value.month}월 필터 제거`}
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
          
          {value.season && (
            <Badge 
              variant="secondary" 
              className="gap-1 pr-1"
            >
              {seasons.find(s => s.value === value.season)?.label}
              <button
                onClick={() => handleClearFilter('season')}
                className="ml-1 rounded-full hover:bg-background/20 p-0.5"
                aria-label={`${value.season} 필터 제거`}
              >
                <X className="w-3 h-3" />
              </button>
            </Badge>
          )}
        </div>
      )}

      {/* Expanded Filter Tips - Desktop Only */}
      {isExpanded && (
        <div className={cn(
          "hidden lg:block p-4 bg-muted/30 rounded-lg space-y-2 text-sm",
          !prefersReducedMotion && "animate-in fade-in slide-in-from-top-2 duration-200"
        )}>
          <p className="font-medium text-foreground">🌿 제철 특산물 찾기 팁</p>
          <ul className="space-y-1 text-muted-foreground ml-4">
            <li>• <strong>봄 (3-5월)</strong>: 산나물, 봄나물, 딸기</li>
            <li>• <strong>여름 (6-8월)</strong>: 고랭지 채소, 옥수수, 감자</li>
            <li>• <strong>가을 (9-11월)</strong>: 버섯, 사과, 단풍나물</li>
            <li>• <strong>겨울 (12-2월)</strong>: 황태, 메밀, 겨울 산나물</li>
          </ul>
          <p className="text-xs text-muted-foreground mt-2">
            💡 지도에서 지역을 선택하면 해당 지역의 제철 특산물을 확인할 수 있습니다.
          </p>
        </div>
      )}
    </div>
  );
}